import React from "react";
import { buildAliasesPayload, buildAliasesSnapshot, buildNewBillingAccountAssociations,
  buildNewBillingAccountAssociationsPayload, NEW_BILLING_ACCOUNT_ID } from "../utils/billingAliases.js";

// One snapshot per open company form; background reads never replace its draft.
export default function useBillingAccountAssociations({ account, accountName, onReload }) {
  const isNew = !account?.id;
  const accountId = account?.id || NEW_BILLING_ACCOUNT_ID;
  const initialAccount = React.useRef(account);
  const initialOwners = React.useRef(null);
  const [draft, setDraft] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    onReload().then((data) => {
      if (!active) return;
      const current = data.accounts.find((row) => row.id === accountId);
      if (!isNew && (!current || current.is_active === false || current.version !== account.version)) {
        setError("Cette entreprise a changé. Rechargez le répertoire puis rouvrez sa fiche.");
        return;
      }
      const snapshot = buildAliasesSnapshot(data);
      const next = isNew ? buildNewBillingAccountAssociations(data, initialAccount.current) : { snapshot, owners: snapshot.owners };
      initialOwners.current = next.owners;
      setDraft(next);
    }).catch((failure) => {
      if (active) setError(failure.message || "Impossible de charger les associations.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accountId, account.version, isNew, onReload]);
  const dirty = Boolean(draft && draft.snapshot.entries.some((row) => draft.owners[row.key] !== initialOwners.current?.[row.key]));
  const displayDraft = React.useMemo(() => !draft || !isNew ? draft : {
    ...draft, snapshot: { ...draft.snapshot, accounts: draft.snapshot.accounts.map((row) => row.id === accountId
      ? { ...row, name: accountName?.trim() || "Nouvelle entreprise" } : row) },
  }, [draft, isNew, accountId, accountName]);
  return { accountId, draft: displayDraft, setDraft, loading, error, dirty,
    payload: draft && isNew ? buildNewBillingAccountAssociationsPayload(draft)
      : dirty ? buildAliasesPayload(draft.snapshot, draft.owners) : undefined,
    ready: Boolean(draft && !loading && !error) };
}
